/* Created by Interactive 63 - https://interactive63.com */
/**
 * A colour field: a swatch and text box that open a popover with a colour picker, an alpha
 * slider, a free-text field and swatches of the colours already used in the document.
 */
import { formatColour, parseColour, type RGBA } from '../../../../core/colour';
import { h } from '../../../ui/dom';
import { createPopover, type Popover } from '../../../ui/popover';

export interface ColourInputOptions {
  /** Called with a new colour string. A coalesce key is given while dragging in the picker. */
  onChange: (value: string, coalesceKey?: string) => void;
  scrubKey?: string;
  /** Colours already used in the document. */
  documentColours: () => string[];
}

const HINT = 'Animating from a gradient or none switches instantly halfway through a segment.';

/** Describes a non-interpolable value for display, such as "Gradient (sky)" or "None". */
export function describeColour(value: string): string {
  const gradient = /^url\(\s*#([^)]+)\)$/.exec(value.trim());
  if (gradient?.[1]) return `Gradient (${gradient[1].replace(/^['"]|['"]$/g, '')})`;
  if (value.trim().toLowerCase() === 'none') return 'None';
  return value;
}

/** Converts a colour to a #rrggbb string for the native picker. */
function hexOf(colour: RGBA): string {
  return formatColour({ ...colour, a: 1 });
}

export class ColourInput {
  readonly el: HTMLElement;
  private readonly swatch: HTMLButtonElement;
  private readonly text: HTMLInputElement;
  private readonly picker: HTMLInputElement;
  private readonly alpha: HTMLInputElement;
  private readonly code: HTMLInputElement;
  private readonly swatches: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly popover: Popover;
  private current: string | null = null;
  private readOnly = false;

  /** Builds the field and its popover. */
  constructor(private readonly options: ColourInputOptions) {
    this.swatch = h('button', {
      type: 'button',
      class: 'colour-swatch',
      'aria-label': 'Open colour picker',
      title: 'Open colour picker',
    });
    this.text = h('input', {
      type: 'text',
      class: 'colour-text',
      'aria-label': 'Colour',
      autocomplete: 'off',
    });
    this.picker = h('input', {
      type: 'color',
      class: 'colour-picker',
      'aria-label': 'Hue and value',
    });
    this.alpha = h('input', {
      type: 'range',
      min: 0,
      max: 100,
      step: 1,
      class: 'colour-alpha',
      'aria-label': 'Alpha',
    });
    this.code = h('input', {
      type: 'text',
      class: 'colour-code',
      'aria-label': 'Colour value',
      autocomplete: 'off',
    });
    this.swatches = h('div', { class: 'colour-swatches' });
    this.hint = h('p', { class: 'colour-hint', hidden: true }, HINT);

    this.el = h('span', { class: 'colour-input' }, this.swatch, this.text);
    this.popover = createPopover(
      this.swatch,
      h(
        'div',
        { class: 'colour-popover' },
        this.picker,
        h('label', { class: 'colour-alpha-row' }, 'Alpha', this.alpha),
        this.code,
        h('div', { class: 'colour-swatches-title' }, 'Document colours'),
        this.swatches,
        this.hint,
      ),
      'Colour picker',
    );
    this.el.append(this.popover.el);
    this.bind();
  }

  /** Shows a colour string, or "Mixed" for null. */
  setValue(value: string | null, force = false): void {
    this.current = value;
    const parsed = value === null ? null : parseColour(value);
    const display = value === null ? '' : parsed ? formatColour(parsed) : describeColour(value);
    if (force || document.activeElement !== this.text) this.text.value = display;
    this.text.placeholder = value === null ? 'Mixed' : '';
    this.swatch.style.setProperty('--swatch', parsed ? formatColour(parsed) : 'transparent');
    this.swatch.classList.toggle('is-empty', parsed === null);
    if (this.popover.isOpen()) this.syncPopover(parsed);
  }

  /** Makes the field read-only. */
  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
    this.text.readOnly = readOnly;
    this.swatch.disabled = readOnly;
    if (readOnly) this.popover.close();
  }

  /** Brings the popover controls in line with the current colour. */
  private syncPopover(parsed: RGBA | null): void {
    this.hint.hidden = parsed !== null;
    if (parsed) {
      this.picker.value = hexOf(parsed);
      this.alpha.value = String(Math.round(parsed.a * 100));
      if (document.activeElement !== this.code) this.code.value = formatColour(parsed);
    } else if (document.activeElement !== this.code) {
      this.code.value = this.current === null ? '' : describeColour(this.current);
    }
  }

  /** Fills the document swatches, called each time the popover opens. */
  private renderSwatches(): void {
    this.swatches.replaceChildren(
      ...this.options.documentColours().map((colour) =>
        h('button', {
          type: 'button',
          class: 'colour-swatch small',
          style: `--swatch: ${colour}`,
          title: colour,
          'aria-label': colour,
          onClick: () => {
            this.options.onChange(colour);
          },
        }),
      ),
    );
  }

  /** Emits a colour built from the native picker and alpha slider. */
  private emitFromPicker(): void {
    const base = parseColour(this.picker.value);
    if (!base) return;
    this.options.onChange(
      formatColour({ ...base, a: Number(this.alpha.value) / 100 }),
      this.options.scrubKey,
    );
  }

  /** Commits typed text from either text field when it is a colour we understand. */
  private commitText(field: HTMLInputElement): void {
    const parsed = parseColour(field.value);
    if (!parsed) {
      this.setValue(this.current);
      field.value = this.current === null ? '' : this.display();
      return;
    }
    const next = formatColour(parsed);
    if (next !== this.current) this.options.onChange(next);
  }

  /** The text shown for the current value. */
  private display(): string {
    if (this.current === null) return '';
    const parsed = parseColour(this.current);
    return parsed ? formatColour(parsed) : describeColour(this.current);
  }

  /** Wires the swatch, text fields and picker controls. */
  private bind(): void {
    this.swatch.addEventListener('click', () => {
      if (this.popover.isOpen()) {
        this.popover.close();
        return;
      }
      this.renderSwatches();
      this.popover.open();
      this.syncPopover(this.current === null ? null : parseColour(this.current));
    });
    for (const field of [this.text, this.code]) {
      field.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.commitText(field);
        } else if (event.key === 'Escape') {
          field.value = this.display();
          field.blur();
        }
      });
      field.addEventListener('blur', () => {
        if (!this.readOnly) this.commitText(field);
      });
      field.addEventListener('focus', () => {
        field.select();
      });
    }
    this.picker.addEventListener('input', () => {
      this.emitFromPicker();
    });
    this.alpha.addEventListener('input', () => {
      this.emitFromPicker();
    });
  }
}
